// The fork in the road, and the only place in the app that takes it.
//
//   demo mode    → the app, on the in-memory dataset. No account, no network, no login screen.
//   configured   → the login screen until there is a session, then the app — or, when the session
//                  check itself could not finish, a screen that says so and offers two ways out.
//
// Why a gate rather than a redirect: the app has no public screens, so there is no route to guard
// and nothing to navigate between. Rendering one or the other means a data hook can never fire
// without a token behind it — the failure that produces a screen full of error states instead of
// a form.

import { useEffect, useRef, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { useSessionCheck } from '../lib/auth/useSession'
import { prefetchCoreScreens } from '../lib/data'
import { Login } from '../routes/Login'

export function AuthGate({ children }: { children: ReactNode }) {
  const { state: session, retry, signInAgain } = useSessionCheck()
  const queryClient = useQueryClient()

  // Fired exactly once per session becoming known — 'demo' fires it immediately (there is no
  // sign-in to wait on), 'signed-in' fires it the instant a session lands. Not on 'loading' or
  // 'signed-out': there is no owner-scoped data to warm until one of those two is true. The ref
  // guards against firing again on every re-render of this component (StrictMode's double-invoke
  // among them) — a second run is harmless (prefetchQuery no-ops on a fresh cache entry) but
  // pointless.
  const firedRef = useRef(false)
  useEffect(() => {
    if (firedRef.current) return
    if (session.status !== 'demo' && session.status !== 'signed-in') return
    firedRef.current = true
    void prefetchCoreScreens(queryClient)
  }, [session.status, queryClient])

  if (session.status === 'loading') {
    // Deliberately not an ellipsis: in this codebase "…" means "a query has not settled", and the
    // test helpers wait on it. This is a different kind of waiting and usually lasts a few
    // milliseconds — at most SESSION_CHECK_TIMEOUT_MS, after which the 'unreachable' screen below
    // takes over.
    return (
      <p
        aria-busy="true"
        className="px-6 py-16 text-center text-sm text-microcopy"
      >
        Checking your session
      </p>
    )
  }

  if (session.status === 'unreachable') {
    return <SessionUnreachable reason={session.reason} onRetry={retry} onSignInAgain={signInAgain} />
  }

  if (session.status === 'signed-out') return <Login />

  return <>{children}</>
}

/**
 * The session check did not finish (timed out, or threw). Not a login form: the owner may well
 * still be signed in, and saying otherwise would fake knowledge. Same layout primitives as Login.
 */
function SessionUnreachable({
  reason,
  onRetry,
  onSignInAgain,
}: {
  reason: string
  onRetry: () => void
  onSignInAgain: () => void
}) {
  return (
    <main className="mx-auto flex min-h-full max-w-sm flex-col justify-center px-6 py-16">
      <img src="/brand/rail-mark.png" alt="" className="size-[30px]" aria-hidden />
      <h1 className="mt-5 text-section font-semibold">LifeOS</h1>
      <p role="alert" className="mt-2 text-body-sm text-ink-2">
        {reason}
      </p>
      <p className="mt-1 text-body-sm text-ink-3">Turn off a VPN, check the connection, then retry.</p>

      <div className="mt-8 space-y-3">
        <button
          type="button"
          onClick={onRetry}
          className="w-full rounded-button bg-ink px-3 py-[10px] text-[13px] font-semibold text-surface transition-opacity duration-[var(--dur-state)] ease-[var(--ease-ink)] hover:opacity-90"
        >
          Retry
        </button>
        <button
          type="button"
          onClick={onSignInAgain}
          className="w-full rounded-button border border-line-2 bg-surface px-3 py-[10px] text-[13px] font-semibold text-ink transition-colors duration-[var(--dur-state)] ease-[var(--ease-ink)] hover:bg-surface-2"
        >
          Sign in again
        </button>
      </div>

      <p className="mt-8 text-[11px] leading-[1.55] text-microcopy">
        Sign in again forgets the session stored in this browser and shows the login form. Nothing
        you have written is touched.
      </p>
    </main>
  )
}
